import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Trash2, RotateCcw, Send, Clock, Video, Heart, EyeOff, Star, Flag } from "lucide-react";
import { VideoModal } from "./VideoModal";
import { toast } from "../../hooks/use-toast";

const SECTIONS = [
  { id: "current", label: "Current" },
  { id: "used", label: "Used" },
  { id: "expired", label: "Expired" },
  { id: "deleted", label: "Deleted" },
];

function categorize(v) {
  if (v.deleted) return "deleted";
  const expired = v.expires_at && new Date(v.expires_at) < new Date() && !v.honored;
  const fullyUsed = v.status === "used" || (v.balance ?? 0) <= 0;
  if (fullyUsed) return "used";
  if (expired) return "expired";
  return "current";
}

export default function IssuedVouchersTab() {
  const [vouchers, setVouchers] = useState(null);
  const [section, setSection] = useState("current");
  const [videosOnly, setVideosOnly] = useState(false);
  const [playing, setPlaying] = useState(null);

  const load = () => api.get("/business/vouchers").then(({ data }) => setVouchers(data.vouchers));
  useEffect(() => { load(); }, []);

  const remove = async (v) => {
    if (!window.confirm(`Delete voucher ${v.code}? You can restore it from the Deleted tab.`)) return;
    await api.delete(`/business/vouchers/${v.id}`);
    toast({ title: "Voucher deleted" });
    load();
  };
  const restore = async (v) => {
    await api.post(`/business/vouchers/${v.id}/restore`);
    toast({ title: "Voucher restored" });
    load();
  };

  const sendNow = async (v) => {
    if (!window.confirm(`Email voucher ${v.code} to ${v.recipient_email || v.buyer_email} now?`)) return;
    try {
      await api.post(`/business/vouchers/${v.id}/send-now`);
      toast({ title: "Voucher emailed" });
      load();
    } catch (err) {
      toast({ title: "Could not send", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };
  const setVisibility = async (v, kind, hidden) => {
    await api.post(`/business/vouchers/${v.id}/video-visibility`, { kind, hidden });
    toast({ title: hidden ? "Video hidden" : "Video restored", description: hidden ? "It's no longer viewable anywhere. You can un-hide it later." : "The video is viewable again." });
    load();
  };
  const setFeatured = async (v, featured) => {
    try {
      await api.post(`/business/vouchers/${v.id}/feature-reaction`, { featured });
      toast({ title: featured ? "Featured on your storefront" : "Removed from storefront" });
      load();
    } catch (err) {
      toast({ title: "Could not update", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };
  const dismissReport = async (v) => {
    await api.post(`/business/vouchers/${v.id}/dismiss-report`);
    toast({ title: "Report dismissed" });
    load();
  };
  const isScheduled = (v) => v.scheduled_send_at && !v.email_sent && new Date(v.scheduled_send_at) > new Date();

  if (!vouchers) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const counts = SECTIONS.reduce((a, s) => ({ ...a, [s.id]: vouchers.filter((v) => categorize(v) === s.id).length }), {});
  const hasVideo = (v) => v.video_url || v.reaction_url;
  const rows = vouchers.filter((v) => categorize(v) === section && (!videosOnly || hasVideo(v)));

  return (
    <div data-testid="issued-vouchers-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)} data-testid={`voucher-section-${s.id}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${section === s.id ? "bg-[#16243b] text-white" : "bg-white text-[#5a6b80] border border-gray-200 hover:border-[#2ec9c9]"}`}>
            {s.label} <span className="opacity-70">({counts[s.id]})</span>
          </button>
        ))}
        <button onClick={() => setVideosOnly((x) => !x)} data-testid="videos-only-filter"
          className={`ml-auto rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${videosOnly ? "bg-[#2ec9c9] text-white" : "bg-white text-[#5a6b80] border border-gray-200 hover:border-[#2ec9c9]"}`}>
          <Video size={14} /> With videos ({vouchers.filter(hasVideo).length})
        </button>
      </div>
      {playing && <VideoModal title={playing.title} url={playing.url} onClose={() => setPlaying(null)} onHide={() => { setVisibility(playing.v, playing.kind, true); setPlaying(null); }} />}
      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Code", "Product", "Buyer", "Value", "Balance", "Expires", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} className="border-t border-gray-50" data-testid={`voucher-row-${v.id}`}>
                <td className="px-5 py-3 font-mono text-[#16243b]">{v.code}
                  {isScheduled(v) && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#eef7ff] text-[#2a4a6b] px-2 py-0.5 text-[11px] font-sans font-medium" data-testid={`scheduled-badge-${v.id}`}><Clock size={11} /> Sends {v.scheduled_send_at.slice(0, 10)}</span>}
                  {v.video_url && v.gift_video_hidden && <HiddenChip label="Message" testid={`unhide-gift-video-${v.id}`} onClick={() => setVisibility(v, "gift", false)} />}
                  {v.reaction_url && v.reaction_hidden && <HiddenChip label="Reaction" testid={`unhide-reaction-video-${v.id}`} onClick={() => setVisibility(v, "reaction", false)} />}
                  {v.video_url && !v.gift_video_hidden && <button onClick={() => setPlaying({ v, kind: "gift", url: v.video_url, title: `Gift message from ${v.buyer_name} · ${v.code}` })} data-testid={`play-gift-video-${v.id}`} title="Watch gift video message" className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#16243b] text-white px-2 py-0.5 text-[11px] font-sans align-middle"><Video size={11} /> Message</button>}
                  {v.video_url && v.gift_video_report && !v.gift_video_hidden && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 text-[11px] font-sans align-middle" title={v.gift_video_report.reason || "No details given"} data-testid={`reported-badge-${v.id}`}>
                      <Flag size={11} /> Reported
                      <button onClick={() => dismissReport(v)} data-testid={`dismiss-report-${v.id}`} className="underline ml-1">Dismiss</button>
                    </span>
                  )}
                  {v.reaction_url && !v.reaction_hidden && v.reaction_feature_consent && (
                    <button onClick={() => setFeatured(v, !v.reaction_featured)} data-testid={`feature-reaction-${v.id}`} title={v.reaction_featured ? "Featured on storefront — click to remove" : "Feature on storefront (recipient consented)"}
                      className={`ml-1 align-middle ${v.reaction_featured ? "text-amber-500" : "text-[#c0c8d2] hover:text-amber-400"}`}>
                      <Star size={15} fill={v.reaction_featured ? "currentColor" : "none"} />
                    </button>
                  )}
                  {v.reaction_url && !v.reaction_hidden && <button onClick={() => setPlaying({ v, kind: "reaction", url: v.reaction_url, title: `Thank-you from ${v.recipient_name || "recipient"} · ${v.code}` })} data-testid={`play-reaction-video-${v.id}`} title="Watch thank-you reaction" className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-500 text-white px-2 py-0.5 text-[11px] font-sans align-middle"><Heart size={11} /> Reaction</button>}
                </td>
                <td className="px-5 py-3 text-[#5a6b80]">{v.product_title}</td>
                <td className="px-5 py-3 text-[#5a6b80]">{v.buyer_name}</td>
                <td className="px-5 py-3 text-[#16243b]">£{(v.original_amount || 0).toFixed(2)}</td>
                <td className="px-5 py-3 text-[#16243b]">£{(v.balance || 0).toFixed(2)}</td>
                <td className="px-5 py-3 text-[#8a97a8]">{v.expires_at ? v.expires_at.slice(0, 10) : "—"}</td>
                <td className="px-5 py-3 text-right">
                  {section === "deleted" ? (
                    <button onClick={() => restore(v)} data-testid={`restore-voucher-${v.id}`} className="text-[#1fb0b0] hover:underline text-xs font-medium inline-flex items-center gap-1"><RotateCcw size={13} /> Restore</button>
                  ) : (
                    <>
                    {(v.recipient_email || v.buyer_email) && <button onClick={() => sendNow(v)} data-testid={`send-now-${v.id}`} title={isScheduled(v) ? "Send now instead of scheduled date" : "Re-send voucher email"} className="text-[#1fb0b0] hover:text-[#16243b] mr-3"><Send size={15} /></button>}
                    <button onClick={() => remove(v)} data-testid={`delete-voucher-${v.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-[#8a97a8]">No {section} vouchers.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HiddenChip({ label, testid, onClick }) {
  return (
    <button onClick={onClick} data-testid={testid} title="Hidden — click to un-hide"
      className="ml-2 inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 text-[#8a97a8] px-2 py-0.5 text-[11px] font-sans align-middle hover:border-[#2ec9c9] hover:text-[#16243b]">
      <EyeOff size={11} /> {label} hidden · Un-hide
    </button>
  );
}
